import React, { useEffect } from "react";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Section } from "@/components/Section";

/**
 * Composant FAQ optimisé SEO / GEO / GSO :
 *  - Accordéon accessible (shadcn / radix)
 *  - Injection de Schema.org FAQPage en JSON-LD pour Google & moteurs génératifs
 *  - Texte clair, conversationnel (favorable aux LLM)
 *
 * Props :
 *  - items: [{ q: string, a: string | ReactNode }]
 *  - eyebrow, title, subtitle: textes de la section
 *  - schemaId: identifiant unique du <script> JSON-LD (utile si plusieurs FAQ par page)
 */
export const FAQ = ({
  items,
  eyebrow = "Questions fréquentes",
  title = "Vos questions, nos réponses",
  subtitle,
  schemaId = "faq-schema",
}) => {
  // Pour le JSON-LD on a besoin de texte brut — on récupère la version texte
  const plainItems = items.map((it) => ({
    q: it.q,
    a: typeof it.a === "string" ? it.a : it.aText || "",
  }));

  useEffect(() => {
    if (typeof document === "undefined") return;
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: plainItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
    let el = document.getElementById(schemaId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = schemaId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const cur = document.getElementById(schemaId);
      if (cur) cur.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaId, JSON.stringify(plainItems)]);

  return (
    <Section eyebrow={eyebrow} title={title} subtitle={subtitle}>
      <div className="grid gap-10 lg:grid-cols-[1fr_2fr] lg:items-start">
        <div className="hidden lg:block">
          <div className="sticky top-28 rounded-3xl bg-brand-sand-wrapper/60 p-8">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate text-brand-cream">
              <HelpCircle className="h-5 w-5" />
            </span>
            <p className="mt-5 font-heading text-2xl font-bold leading-snug text-brand-slate-dark">
              Une question reste sans réponse ?
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Notre équipe est à votre écoute, par téléphone ou par mail. Nous vous répondons sous
              48 h ouvrées.
            </p>
            <a
              href="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-slate px-5 py-2.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-slate-dark"
            >
              Nous contacter
            </a>
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b border-black/10"
            >
              <AccordionTrigger className="py-5 text-left font-heading text-base font-semibold text-brand-slate-dark hover:no-underline sm:text-lg">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {typeof it.a === "string" ? <p>{it.a}</p> : it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
};

export default FAQ;
